/**
 * Memorial workspace — structured video links with visibility and reorder.
 */
import React from 'react';
import { ArrowDown, ArrowUp, Plus, Trash2, Video } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { VIDEO_LINK_HINT } from '../../utils/videoLink';
import { detectMusicPlatform, newVideoLinkItem } from '../../utils/musicLink';

function moveItem(list, index, direction) {
  const next = [...list];
  const target = index + direction;
  if (target < 0 || target >= next.length) return list;
  [next[index], next[target]] = [next[target], next[index]];
  return next.map((item, i) => ({ ...item, sort_order: i }));
}

const PLATFORM_LABELS = {
  youtube: 'YouTube',
  vimeo: 'Vimeo',
  tiktok: 'TikTok',
  instagram: 'Instagram',
  facebook: 'Facebook',
  loom: 'Loom',
  external: 'Other',
};

export function MemorialVideoLinksPanel({ form, setForm }) {
  const links = form.video_links || [];

  const updateLinks = (next) => {
    setForm((p) => ({ ...p, video_links: next }));
  };

  const addLink = () => {
    updateLinks([...links, newVideoLinkItem({ sort_order: links.length })]);
  };

  const updateLink = (index, patch) => {
    const next = links.map((item, i) => (i === index ? { ...item, ...patch } : item));
    updateLinks(next);
  };

  const removeLink = (index) => {
    updateLinks(links.filter((_, i) => i !== index).map((item, i) => ({ ...item, sort_order: i })));
  };

  return (
    <div className="rounded-2xl border border-[hsl(38_14%_86%)]/60 bg-[hsl(42_28%_99%)]/85 p-5" data-testid="memorial-videos-panel">
      <div className="flex items-start gap-3">
        <Video className="mt-0.5 h-4 w-4 text-[#8B7355]" aria-hidden />
        <div>
          <h3 className="font-serif text-base font-light text-[#1c1917]">Videos</h3>
          <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground/90">{VIDEO_LINK_HINT}</p>
          <p className="mt-1 text-[11px] text-muted-foreground">Links only — no video uploads in this pass. YouTube and Vimeo may preview when safe.</p>
        </div>
      </div>

      <div className="mt-4 space-y-4">
        {links.length === 0 ? (
          <p className="text-sm text-muted-foreground">No video links added yet.</p>
        ) : (
          links.map((item, index) => (
            <div key={item.id || `video-${index}`} className="rounded-xl border border-border/50 bg-white/70 p-4 space-y-3">
              <div className="flex items-center justify-end gap-1">
                <Button type="button" variant="ghost" size="icon" className="h-8 w-8" disabled={index === 0} onClick={() => updateLinks(moveItem(links, index, -1))} aria-label="Move up">
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button type="button" variant="ghost" size="icon" className="h-8 w-8" disabled={index === links.length - 1} onClick={() => updateLinks(moveItem(links, index, 1))} aria-label="Move down">
                  <ArrowDown className="h-4 w-4" />
                </Button>
                <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => removeLink(index)} aria-label="Remove video">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-1.5">
                <Label>Title</Label>
                <Input value={item.title || ''} onChange={(e) => updateLink(index, { title: e.target.value })} placeholder="Memorial service, birthday speech…" />
              </div>

              <div className="space-y-1.5">
                <Label>Video URL</Label>
                <Input
                  value={item.video_url || ''}
                  onChange={(e) => updateLink(index, { video_url: e.target.value })}
                  placeholder="https://youtube.com/…"
                />
              </div>

              <div className="space-y-1.5">
                <Label>Note (optional)</Label>
                <Textarea
                  value={item.description || ''}
                  onChange={(e) => updateLink(index, { description: e.target.value })}
                  rows={2}
                  className="resize-none"
                />
              </div>

              <label className="flex items-center justify-between gap-3">
                <span className="text-sm">Visible on public page</span>
                <Switch
                  checked={item.visible_publicly !== false}
                  onCheckedChange={(v) => updateLink(index, { visible_publicly: v })}
                />
              </label>

              {item.video_url ? (
                <p className="text-[10px] text-muted-foreground">
                  Platform: {PLATFORM_LABELS[item.platform] || PLATFORM_LABELS[detectMusicPlatform(item.video_url)] || 'Will detect on save'}
                </p>
              ) : null}
            </div>
          ))
        )}

        <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={addLink}>
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Add video link
        </Button>
      </div>
    </div>
  );
}

export default MemorialVideoLinksPanel;
